// native
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

// services
import { DevicesService } from 'app/core/services/devices.service';
import { AuthService } from 'app/core/services/auth.service';
import { TestsService } from 'app/core/services/tests.service';
import { DialogService } from 'app/core/services/dialog.service';
import { UtilityService } from 'app/core/services/utility.service';
import { ErrorService } from 'app/core/services/error.service';
import { PatientsService } from 'app/core/services/patients.service';

// models
import { Device, PaginatedItems, ListItemColumn, ListItemAction, ListItemMarker } from '../models/';

// constants
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '../constants';

interface DeviceDisplay extends Device {
  is_running_test: boolean;
  patient_name: string;
  claimed: string;
}

@Component({
    selector: 'app-devices',
    templateUrl: './devices.component.html',
    standalone: false
})
export class DevicesComponent implements OnInit, OnDestroy {

  columns: ListItemColumn[] = [
    {
      translationKey: 'deviceName',
      fieldName: 'name',
      fontClass: 'bold'
    },
    {
      translationKey: 'deviceType',
      fieldName: 'device_type',
      fontClass: 'bold'
    },
    {
      translationKey: 'runningTest',
      fieldName: 'patient_name',
      fontClass: 'bold',
      hoverOnly: false
    },
    {
      translationKey: 'deviceClaimed',
      fieldName: 'claimed',
      fontClass: 'thin',
    },
  ];

  actions: ListItemAction[] = [
    {
      translationKey: 'edit',
      execute: (device: DeviceDisplay) => this.edit(device),
      visible: (device: DeviceDisplay) => this.canEdit(device)
    },
    {
      translationKey: 'cancelTest',
      execute: (device: DeviceDisplay) => this.cancelTest(device),
      visible: (device: DeviceDisplay) => !this.authService.isClinicalTrialReadOnlyNoPii && device.is_running_test
    },
    {
      translationKey: 'unlink',
      execute: (device: DeviceDisplay) => this.unlink(device),
      visible: (device: DeviceDisplay) => this.authService.isDevMode
    },
    {
      translationKey: 'delete',
      execute: (device: DeviceDisplay) => this.deleteDevice(device),
      visible: (device: DeviceDisplay) => this.canDelete(device)
    }
  ];

  orangeMarker: ListItemMarker = {
    visible: (device: DeviceDisplay) => device.is_running_test
  };

  devices$: BehaviorSubject<PaginatedItems<DeviceDisplay> | null> = new BehaviorSubject(null);

  currentPageSize: number = DEFAULT_PAGE_SIZE;
  currentPageIndex: number = DEFAULT_PAGE_INDEX;
  term: string;

  isLoading: boolean = false;

  languageSubscription: Subscription;

  constructor(
    public router: Router,
    public devicesService: DevicesService,
    public authService: AuthService,
    private testsService: TestsService,
    private translateService: TranslateService,
    private dialogService: DialogService,
    private utilityService: UtilityService,
    private toastService: ToastrService,
    private errorService: ErrorService,
    private patientsService: PatientsService
  ) { }

  ngOnInit() {
    this.getDevices(this.currentPageSize, this.currentPageIndex, null);

    this.languageSubscription = this.translateService.onLangChange.subscribe(res => {
      this.getDevices(this.currentPageSize, this.currentPageIndex, null);
    });
  }

  private getDevices(pageSize: number, pageIndex: number, term: string) {
    this.isLoading = true;
    this.devicesService.getAll(pageSize, pageIndex, term).subscribe(res => {
      this.isLoading = false;

      this.devices$.next(this.parseDevices(res));
    }, err => { this.isLoading = false; });
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }

  canEdit(device: Device): boolean {
    if (this.authService.isOfficeGroupAdmin)
      return true;

    if (!this.authService.isOfficeAdmin)
      return false;

    const deviceEditable = this.utilityService.hasCommonElement(
      this.authService.getUserAdministeredOffices(),
      device.offices);
    return deviceEditable;
  }

  canDelete(device: Device): boolean {
    return (this.authService.isOfficeGroupAdmin || this.authService.isOfficeAdmin)
      && !this.authService.isClinicalTrialReadOnlyNoPii
      && !device.user;
  }

  edit(device: Device) {
    this.router.navigate(['/devices/edit', device.id]);
  }

  deleteDevice(device: Device) {
    this.dialogService.openConfirm({
      action: this.translateService.instant('delete'),
      message: this.translateService.instant('areYouSure') + ' '
        + this.translateService.instant('deleteDevicePrompt'),
      text: this.translateService.instant('device') + ': ' + device.name,
      confirmText: this.translateService.instant('yes'),
      cancelText: this.translateService.instant('no')
    }).then(result => {
      if (result.confirmed) {
        this.isLoading = true;
        this.devicesService.delete(device.id).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe(
          () => this.getDevices(this.currentPageSize, this.currentPageIndex, this.term),
          error => this.errorService.handleError(error)
        );
      }
    });
  }

  unlink(device: Device) {
    this.dialogService.openConfirm({
      action: this.translateService.instant('unlink'),
      message: this.translateService.instant('areYouSure') + ' ' + this.translateService.instant('unlink')
        + ' ' + this.translateService.instant('device')?.toLowerCase() + '?',
      text: this.translateService.instant('device') + ': ' + device.name,
      confirmText: this.translateService.instant('yes'),
      cancelText: this.translateService.instant('no')
    }).then(result => {
      if (result.confirmed) {
        this.isLoading = true;
        this.devicesService.unlinkDevMode(device.id).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe(
          res => {
            this.toastService.success(this.translateService.instant('deviceUnlinkSuccess'));
            this.getDevices(this.currentPageSize, this.currentPageIndex, this.term);
          },
          error => this.errorService.handleDeviceError(error)
        );
      }
    });
  }

  search(term: string) {
    this.currentPageIndex = 0;
    this.isLoading = true;

    this.devicesService.getAll(this.currentPageSize, this.currentPageIndex, term).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(res => {
      this.term = term;
      this.devices$.next(this.parseDevices(res));
    });
  };

  create() {
    this.router.navigate(['/devices/new']);
  }

  cancelTest(device: DeviceDisplay) {
    this.dialogService.openConfirm({
      action: this.translateService.instant('delete'),
      message: this.translateService.instant('areYouSure') + ' ' + this.translateService.instant('cancelTest') +
        ' (' + this.translateService.instant('patient') + ' ' + this.patientsService.getDisplayName(device?.patient) + ') ?',
      cancelText: this.translateService.instant('back')
    }).then(result => {
      if (result.confirmed)
        this.testsService.delete(device.test).subscribe(
          res => this.getDevices(this.currentPageSize, this.currentPageIndex, this.term));
    });
  }

  changePage(pageIndex: number, pageSize: number) {
    this.currentPageIndex = pageIndex;
    this.currentPageSize = pageSize;
    this.getDevices(pageSize, pageIndex, this.term);
  }

  private parseDevices(devices: PaginatedItems<Device>): PaginatedItems<DeviceDisplay> {
    const parsedDevices = devices.results.map(device => {
      const { device_type, patient, user } = device;
      const claimed = user ? this.translateService.instant('claimed') : this.translateService.instant('unclaimed');
      const patient_name = this.patientsService.getDisplayName(patient);
      const is_running_test = !!patient;

      return {
        ...device,
        claimed,
        patient_name,
        is_running_test,
        device_type: this.devicesService.getDeviceTypeTranslatedName(device_type),
      };
    });

    return {
      results: parsedDevices,
      count: devices.count
    };
  }
}
